<script setup lang="ts">
import { faEdit, faSync } from "@fortawesome/pro-light-svg-icons";
import { faCheck, faCopy, faTrash } from "@fortawesome/pro-solid-svg-icons";
import type { AppTrigger } from "~/models/App";

const store = useAppStore();
const api = useApi();
const { echo } = useAppEcho();
const triggers = computed(() => store.app?.triggers ?? []);
const { copy, copied } = useClipboard({

    legacy: true,
});

function dropdown(item: any) {
    const items = [];

    items.push({
        label: "Edit",
        icon: faEdit,
        click: () => editTrigger(item)
    });

    return [items];
}

function editTrigger(trigger: AppTrigger) {
    store.editing = "trigger";
    store.trigger = trigger;
    navigateTo(`/apps/${store.app?.uuid}/edit`);
}
</script>

<template>
    <div
        class="col-span-6 max-w-3xl mx-auto 4xl:col-span-5 min-[2256px]:col-span-4 4xl:col-start-2 min-[2256px]:col-start-3 flex flex-col gap-2 w-full">
        <h3 class="font-display font-medium">Credentials</h3>
        <UIDataList ui="bg-white border border-gray-300 rounded-md mb-2">
            <UIDataListItem condensed>
                <template #label>App ID</template>
                <template #value>
                    <div class="flex items-center gap-4 flex-1 pt-1">
                        <UInput
                            :model-value="store.app.uuid"
                            class="flex-1"
                            color="gray"
                            disabled
                        />
                        <UICopy
                            v-slot="{ copy, copied }"
                            :source="store.app.uuid"
                        >
                            <UButton
                                class="flex-shrink-0 w-8 h-8 justify-center"
                                color="gray"
                                square
                                @click="copy(store.app.uuid)"
                            >
                                <FontAwesomeIcon
                                    :icon="
                                                    copied ? faCheck : faCopy
                                                "
                                />
                            </UButton>
                        </UICopy>
                    </div>
                </template>
            </UIDataListItem>
            <UIDataListItem v-if="store.app.client" condensed>
                <template #label>Client ID</template>
                <template #value>
                    <div class="flex items-center gap-4 flex-1">
                        <UInput
                            :model-value="store.app.client.id"
                            class="flex-1"
                            color="gray"
                            disabled
                        />
                        <UICopy
                            v-slot="{ copy, copied }"
                            :source="store.app.client.id"
                        >
                            <UButton
                                class="flex-shrink-0 w-8 h-8 justify-center"
                                color="gray"
                                square
                                @click="copy(store.app.client.id)"
                            >
                                <FontAwesomeIcon
                                    :icon="
                                                    copied ? faCheck : faCopy
                                                "
                                />
                            </UButton>
                        </UICopy>
                    </div>
                </template>
            </UIDataListItem>
            <UIDataListItem v-if="store.app.client" condensed>
                <template #label>Client Secret</template>
                <template #value>
                    <div class="flex items-center gap-4 flex-1 pb-1">
                        <UInput
                            :model-value="store.app.client.secret.split('').map(() => '•').join('')"
                            class="flex-1"
                            color="gray"
                            disabled
                        />
                        <UICopy
                            v-slot="{ copy, copied }"
                            :source="store.app.client.secret"
                        >
                            <UButton
                                class="flex-shrink-0 w-8 h-8 justify-center"
                                color="gray"
                                square
                                @click="
                                                copy(store.app.client.secret)
                                            "
                            >
                                <FontAwesomeIcon
                                    :icon="
                                                    copied ? faCheck : faCopy
                                                "
                                />
                            </UButton>
                        </UICopy>
                    </div>
                </template>
            </UIDataListItem>
        </UIDataList>

        <h3 class="font-display font-medium">Triggers</h3>
        <ClientOnly>
            <UiFocusableButton
                v-if="triggers.length === 0"
                class="bg-white"
                :label="`Edit app and create your first trigger`"
                :img="store.app.icon"
                :to="{
                    name: 'apps-id-edit',
                    params: { id: store.app.uuid }
                }"
                bordered
            />
            <UAccordion
                :items="
                            triggers.map((item, index) => ({
                                ...item,
                                defaultOpen: triggers.length === 1,
                            }))
                        "
                class="w-full [&>button]:active:scale-100 gap-2"
                :ui="{
                container:
                    'flex rounded-md flex-col w-full bg-white ring-1 ring-inset ring-gray-300 shadow-sm',
                item: {
                    base: 'px-3 py-2',
                },
            }"
            >
                <template #item="{ item }">
                    <UTabs
                        :items="[
                            { label: 'General', slot: 'general' },
                            ...(item.sample_object ? [{ label: 'Sample Object', slot: 'sample', }] : []),
                            { label: 'Fields', slot: 'fields' },
                        ]"
                    >
                        <template #general>
                            <UFormGroup label="Webhook URL" help="This is the URL you will send data to for this trigger.">
                                <div class="flex items-center text-gray-900 gap-2 border-l border-gray-300 pl-3">
                                    <span class="font-medium">{{ item.method }}</span>
                                    <UInput
                                        class="flex-1"
                                        size="md"
                                        disabled
                                        :model-value="item.webhook_url"
                                    />
                                    <UButton
                                        square
                                        class="flex-shrink-0 w-9 h-9 justify-center"
                                        color="gray"
                                        @click="copy(item.webhook_url)"
                                    >
                                        <FontAwesomeIcon
                                            :icon="copied ? faCheck : faCopy"
                                        />
                                    </UButton>
                                </div>
                            </UFormGroup>
                        </template>
                        <template #sample>
                            <UiDataDisplay :data="item.sample_object" />
                        </template>
                        <template #fields>
                            <AppFieldDataList
                                ref="dataList"
                                class="flex-1 max-h-[calc(100%-3rem)] min-h-0 overflow-y-auto"
                                :fields="item.object_definition"
                                no-copy
                            />
                        </template>
                    </UTabs>
                </template>
                <template #default="{ item, index, open }">
                    <div class="flex items-center px-3 gap-2">
                        <UButton
                            color="white"
                            variant="ghost"
                            :padded="false"
                            class="!scale-100 !bg-transparent py-2 flex-1 !ring-0 !shadow-none"
                        >
                            <template #leading>
                                <UButton color="white" variant="link" @click="editTrigger(item)">
                                    <BadgeAutomationStatus
                                        :automation="item"
                                    />
                                </UButton>
                            </template>

                            <span class="truncate">{{
                                    item.label
                                }}</span>
                        </UButton>
                        <UiDropdown :items="dropdown(item)">
                            <template #default="{ toggle }">
                                <UButton
                                    color="gray"
                                    icon="i-heroicons-ellipsis-horizontal-20-solid"
                                    variant="ghost"
                                    @click.stop="toggle"
                                />
                            </template>
                        </UiDropdown>
                        <UIcon
                            name="i-heroicons-chevron-right-20-solid"
                            class="w-5 h-5 ms-auto transform transition-transform duration-200"
                            :class="[open && 'rotate-90']"
                        />
                    </div>
                </template>
            </UAccordion>
        </ClientOnly>
    </div>
</template>

<style scoped>

</style>